import {
  type IapGateway,
  type StoreProduct,
  type StorePurchase,
  nativeIapGateway,
} from '../native/iap';
import {
  type SubscriptionProductId,
  type SubscriptionStoreTarget,
  isSubscriptionProductForTarget,
  subscriptionProductIdFor,
} from './catalog';

export type SubscriptionStoreProduct = StoreProduct & {
  productId: SubscriptionProductId;
};

function isUsableSubscriptionProduct(
  product: StoreProduct,
  target: SubscriptionStoreTarget,
): product is SubscriptionStoreProduct {
  return (
    isSubscriptionProductForTarget(product.productId, target) &&
    product.productType === 'subs' &&
    product.title.trim().length > 0 &&
    product.description.trim().length > 0
  );
}

/**
 * Loads only the SKU declared in the checked-in catalogue. Unknown products,
 * incomplete store metadata, and duplicate product rows are rejected rather
 * than letting an accidental dashboard SKU reach a guardian purchase surface.
 */
export async function loadSubscriptionStorefront(
  target: SubscriptionStoreTarget,
  gateway: Pick<IapGateway, 'getProducts'> = nativeIapGateway,
): Promise<SubscriptionStoreProduct[]> {
  const productId = subscriptionProductIdFor(target);
  const response = await gateway.getProducts([productId], 'subs');
  const products = response.products.filter((product) =>
    isUsableSubscriptionProduct(product, target),
  );
  if (products.length !== 1) {
    throw new Error('The ChorePop Family subscription is not available from this store.');
  }
  if (new Set(products.map((product) => product.productId)).size !== products.length) {
    throw new Error('The store returned duplicate ChorePop subscription products.');
  }
  return products;
}

export async function purchaseChorePopFamily(
  appAccountToken: string,
  target: SubscriptionStoreTarget,
  gateway: Pick<IapGateway, 'purchase'> = nativeIapGateway,
): Promise<StorePurchase> {
  if (!/^[0-9a-f]{8}-(?:[0-9a-f]{4}-){3}[0-9a-f]{12}$/iu.test(appAccountToken)) {
    throw new Error('A verified ChorePop guardian is required before subscribing.');
  }
  const digest = await crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(appAccountToken.toLowerCase()),
  );
  const obfuscatedAccountId = [...new Uint8Array(digest)]
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
  const productId = subscriptionProductIdFor(target);
  const purchase = await gateway.purchase(productId, 'subs', {
    appAccountToken,
    obfuscatedAccountId,
  });
  if (
    purchase.productId !== productId ||
    purchase.purchaseState !== 0 ||
    !purchase.purchaseToken.trim()
  ) {
    throw new Error('The store did not complete the ChorePop Family subscription.');
  }
  return purchase;
}

export async function restoreChorePopFamily(
  target: SubscriptionStoreTarget,
  gateway: Pick<IapGateway, 'restorePurchases'> = nativeIapGateway,
): Promise<StorePurchase> {
  const productId = subscriptionProductIdFor(target);
  const restored = await gateway.restorePurchases('subs');
  const subscription = restored.purchases.find(
    (purchase) =>
      purchase.productId === productId &&
      purchase.purchaseState === 0 &&
      purchase.purchaseToken.trim().length > 0,
  );
  if (!subscription) throw new Error('No active ChorePop Family subscription was found.');
  return subscription;
}

/** Acknowledge only after trusted verification and cloud migration succeed. */
export async function acknowledgeChorePopFamily(
  purchaseToken: string,
  gateway: Pick<IapGateway, 'acknowledgePurchase'> = nativeIapGateway,
): Promise<void> {
  if (!purchaseToken.trim())
    throw new Error('The store returned no purchase token to acknowledge.');
  await gateway.acknowledgePurchase(purchaseToken);
}
